/**
 * Persona Kütüphanesi store'u — `useDebateStore`'dan AYRI tutulur.
 *
 * Gerekçe: `useDebateStore` `persist` middleware'i ile `macdm-session`
 * anahtarına yazıyor ve tek bir tartışmanın uçucu durumunu taşıyor. Kütüphane
 * ise oturumdan bağımsız, sunucu-otoriteli bir katalog — onu o store'a
 * koymak (a) her tartışma sıfırlamasında listeyi de sıfırlar, (b) yüzlerce
 * personayı localStorage'a yazmayı riske atardı. `authStore` ile aynı
 * gerekçeyle düz `create()` kullanılır; immer'a ihtiyaç duyacak iç içe
 * mutasyon yok.
 */
import { create } from "zustand";
import type {
  LibraryCatalog,
  LibraryPersonaBrief,
  PersonaTeam,
  PersonaUnit,
} from "../domain/personaLibrary";
import {
  LibraryApiError,
  fetchCatalog,
  listPersonas,
  listTeams,
  listUnits,
} from "../transport/personaLibraryClient";

interface PersonaLibraryState {
  catalog: LibraryCatalog | null;
  personas: LibraryPersonaBrief[];
  teams: PersonaTeam[];
  units: PersonaUnit[];
  unassignedCount: number;
  loading: boolean;
  /** Kütüphane sunucuda kapalı (feature flag) — UI tamamen gizlenmeli. */
  unavailable: boolean;
  error: string | null;
  /** İlk yükleme denemesinden sonra true; tekrar tekrar fetch etmeyi önler. */
  loaded: boolean;

  load(opts?: { force?: boolean }): Promise<void>;
  refreshPersonas(search?: string): Promise<void>;
  refreshTeams(): Promise<void>;
  refreshUnits(): Promise<void>;
  clearError(): void;
}

function describe(err: unknown): string {
  if (err instanceof LibraryApiError) {
    // 422 gövdesindeki `details` alan adını taşıyor; onsuz "request
    // validation failed" mesajı teşhis için tamamen değersiz.
    const detail = err.details;
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { loc?: unknown[]; msg?: string };
      const where = Array.isArray(first.loc) ? first.loc.join(".") : "";
      return `${err.message}${where ? ` (${where}: ${first.msg ?? ""})` : ""}`;
    }
    return err.message;
  }
  return err instanceof Error ? err.message : "Bilinmeyen hata";
}

export const usePersonaLibraryStore = create<PersonaLibraryState>((set, get) => ({
  catalog: null,
  personas: [],
  teams: [],
  units: [],
  unassignedCount: 0,
  loading: false,
  unavailable: false,
  error: null,
  loaded: false,

  load: async (opts) => {
    if (get().loading) return;
    if (get().loaded && !opts?.force) return;
    set({ loading: true, error: null });

    // BULUNAN HATA (canlı): burada `Promise.all` kullanılıyordu. Persona
    // listesi bir rota gölgelemesi yüzünden 422 dönünce TÜM yükleme
    // reddediliyor ve — hiç ilgisi olmadığı hâlde — eksen kataloğu da null
    // kalıyordu; kullanıcı bunu "Mizaç sekmesi boş" olarak görüyordu.
    // `allSettled` ile her kaynak bağımsız yaşar: biri düşerse yalnız o
    // bölüm boş kalır ve hata bandı hangisinin düştüğünü söyler.
    const [catalogR, personasR, teamsR, unitsR] = await Promise.allSettled([
      fetchCatalog(),
      listPersonas(),
      listTeams(),
      listUnits(),
    ]);

    const failures: string[] = [];
    const rejected = [catalogR, personasR, teamsR, unitsR].filter(
      (r): r is PromiseRejectedResult => r.status === "rejected",
    );
    // Hepsi 404/503 ise özellik kapalıdır — bunu "hata" diye göstermek
    // yanıltıcı olur; menüyü gizleyip sessizce geçmek doğru davranış.
    const allUnavailable =
      rejected.length === 4 &&
      rejected.every((r) => r.reason instanceof LibraryApiError && r.reason.isUnavailable);

    const next: Partial<PersonaLibraryState> = {
      loading: false,
      loaded: true,
      unavailable: allUnavailable,
    };

    if (catalogR.status === "fulfilled") next.catalog = catalogR.value;
    else failures.push(`Ayar kataloğu: ${describe(catalogR.reason)}`);

    if (personasR.status === "fulfilled") next.personas = personasR.value;
    else failures.push(`Personalar: ${describe(personasR.reason)}`);

    if (teamsR.status === "fulfilled") next.teams = teamsR.value;
    else failures.push(`Takımlar: ${describe(teamsR.reason)}`);

    if (unitsR.status === "fulfilled") {
      next.units = unitsR.value.units;
      next.unassignedCount = unitsR.value.unassignedCount;
    } else {
      failures.push(`Birimler: ${describe(unitsR.reason)}`);
    }

    next.error = allUnavailable || failures.length === 0 ? null : failures.join(" · ");
    set(next);
  },

  refreshPersonas: async (search) => {
    try {
      const personas = await listPersonas(search ? { search } : undefined);
      set({ personas, error: null });
    } catch (err) {
      set({ error: describe(err) });
    }
  },

  refreshTeams: async () => {
    try {
      set({ teams: await listTeams(), error: null });
    } catch (err) {
      set({ error: describe(err) });
    }
  },

  refreshUnits: async () => {
    try {
      const data = await listUnits();
      set({ units: data.units, unassignedCount: data.unassignedCount, error: null });
    } catch (err) {
      set({ error: describe(err) });
    }
  },

  clearError: () => set({ error: null }),
}));
